import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { AiFillSave } from 'react-icons/ai';

import {
    QUERIES_ADMIN,
    ROUTES_ACCOUNT,
    ROUTES_ADMIN,
} from '@constants';

import { useRealState } from 'contexts/RealStateContext';

import { Button, Loading, Shadow } from 'components/atoms';
import { OptionBlock, InputText } from 'components/molecules';

import { onErrorMessage, onSuccessMessage, onValidationSchema } from 'helpers';

import {
    createAdministratorMethodsPayments,
    getAdministratorMethodsPaymentsShow,
    updateAdministratorMethodsPayments,
} from 'services';

import { MethodsPaymentsFormWrap } from './styles';
import Layout from '../../Layout';

import type { IAPaymentMethod } from '@types';

const MethodsPaymentsForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const queryClient = useQueryClient();

    const { getLinkWithSlug } = useRealState();

    const [step, setStep] = useState('GENERAL_INFO');

    const [nome, setNome] = useState('')
    const [numero, setNumero] = useState('')

    const titleMessages = 'Formas de Pagamentos!';

    const { data: dataDetail, isLoading: isLoadingGet } = useQuery(
        [QUERIES_ADMIN.METHODS_PAYMENTS.base, id],
        () => getAdministratorMethodsPaymentsShow(String(id)),
        {
            enabled: !!id,
        }
    );

    useEffect(() => {
        if (dataDetail) {
            setNome(dataDetail?.nome);
            setNumero(dataDetail?.numero);
        }
    }, [dataDetail]);

    const { mutate: mutateCreate, isLoading: isLoadingCreate } = useMutation(
        (payload: Partial<IAPaymentMethod>) => createAdministratorMethodsPayments(payload),
        {
            mutationKey: [QUERIES_ADMIN.METHODS_PAYMENTS.base, id],
            onSuccess: () => {
                onSuccessMessage(
                    titleMessages,
                    'Sucesso ao criar novo registro!'
                );

                queryClient.refetchQueries(QUERIES_ADMIN.METHODS_PAYMENTS.base);

                navigate(getLinkWithSlug(ROUTES_ADMIN.METHODS_PAYMENTS.fullPath));
            },
            onError: (err) => onErrorMessage(titleMessages, err),
        }
    );

    const { mutate: mutateUpdate, isLoading: isLoadingUpdate } = useMutation(
        (payload: Partial<IAPaymentMethod>) => updateAdministratorMethodsPayments(String(id), payload),
        {
            mutationKey: [QUERIES_ADMIN.METHODS_PAYMENTS.base, id],
            onSuccess: () => {
                onSuccessMessage(
                    titleMessages,
                    'Sucesso ao atualizar o registro!'
                );

                queryClient.refetchQueries(QUERIES_ADMIN.METHODS_PAYMENTS.base);
                queryClient.refetchQueries([QUERIES_ADMIN.METHODS_PAYMENTS.base, id]);
            },
            onError: (err) => {
                onErrorMessage(titleMessages, err);
            },
        }
    );

    const onSubmit = async () => {
        const data = {
            nome,
            numero,
        };

        const shapeSchema = {
            numero: Yup.string().nullable().required('O número é obrigatório.'),
            nome: Yup.string().nullable().required('O nome é obrigatório.'),
        };

        const { success } = await onValidationSchema(shapeSchema, data);

        if (success) {
            if (!id) {
                mutateCreate(data);
            } else {
                mutateUpdate(data);
            }
        }
    };

    return (
        <Layout
            titlePage="Painel Administrativo"
            breadcrumbs={[
                {
                    label: 'Home',
                    href: getLinkWithSlug(ROUTES_ACCOUNT.SELECTED.fullPath),
                },
                {
                    label: 'Formas de Pagamentos',
                    href: getLinkWithSlug(ROUTES_ADMIN.METHODS_PAYMENTS.fullPath),
                },
                {
                    label: 'Formulário Formas de Pagamentos',
                    href: getLinkWithSlug(ROUTES_ADMIN.METHODS_PAYMENTS.FORM.fullPath),
                },
            ]}
            title="Formulário Formas de Pagamentos"
            buttonTitle={
                <Button color="denary" onClick={() => onSubmit()} loading={isLoadingGet || isLoadingUpdate || isLoadingCreate}>
                    <AiFillSave />
                    Salvar
                </Button>
            }
        >
            <MethodsPaymentsFormWrap className="administrator-panel-methods-payments-form">
                <Shadow>
                    <Loading active={isLoadingGet || isLoadingUpdate || isLoadingCreate} />

                    <OptionBlock
                        active={step}
                        onHandleActive={(param) => setStep(param)}
                        data={[
                            {
                                label: 'Informações Gerais',
                                value: 'GENERAL_INFO',
                            },
                        ]}
                        style={{ margin: '0 auto 0 0' }}
                    />

                    <InputText
                        model="sinary"
                        id="nome"
                        required
                        label="Nome"
                        name="nome"
                        placeholder="Informe o nome"
                        value={nome}
                        onChange={setNome}
                    />

                    <InputText
                        model="sinary"
                        id="numero"
                        required
                        label="Número"
                        name="numero"
                        placeholder="Informe o número"
                        value={numero}
                        onChange={setNumero}
                    />
                </Shadow>
            </MethodsPaymentsFormWrap>
        </Layout>
    );
};

export default MethodsPaymentsForm;
